/* ============================================================================
   custom-shared.js — Consolidated section IIFEs
   ============================================================================
   Single bundle replacing the inline <script> blocks formerly emitted by
   seven sections. Each module bails if its target isn't on the current
   page — safe to load once globally via theme.liquid (deferred).

   Modules
     1. anchorButtons     hero + careers-hero same-page-anchor click feedback
     2. footerAccordion   footer column open/close + responsive mq sync
     3. klaviyoCookies    cookie-prefs button → window.klaviyo.openCookiePreferences()
     4. careersSlider     careers-team-cards horizontal slider arrows
     5. pdpEsFeatures     IntersectionObserver fade-in on PDP-ES feature blocks
     6. pdpFaqAccordions  PDP-ES + PDP-PL FAQ click-to-expand (shared pattern)

   Re-entry: boot() runs on DOMContentLoaded and on shopify:section:load
   (so Theme Editor reloads re-bind handlers to fresh DOM). Modules use
   per-element __bettrInit flags to avoid double-binding the same node;
   stale closures from a prior Theme Editor render persist for the editing
   session and clear on full page refresh — acceptable trade-off (zero
   impact in production where sections never reload).
   ============================================================================ */
(function () {
  'use strict';

  /* ── 1. Anchor button click feedback (hero + careers-hero) ─────────────── */
  function initAnchorButtons() {
    var sections = document.querySelectorAll('.custom-hero, .custom-careers-hero');
    Array.prototype.forEach.call(sections, function (section) {
      var btns = section.querySelectorAll('a[href^="#"]');
      Array.prototype.forEach.call(btns, function (btn) {
        if (btn.__bettrAnchorInit) return;
        btn.__bettrAnchorInit = true;
        btn.addEventListener('click', function () {
          btn.classList.add('is-deactivated');
          btn.blur();
          var clear = function () { btn.classList.remove('is-deactivated'); };
          document.addEventListener('mousemove', clear, { once: true });
          document.addEventListener('touchstart', clear, { once: true, passive: true });
        });
      });
    });
  }

  /* ── 2. Footer responsive accordion ──────────────────────────────────────
     On desktop (>=750px): all columns force-open (no click-to-toggle).
     On mobile: all columns force-closed; user expands by clicking. The
     Liquid template renders <details open> by default so JS-disabled
     mobile users still see expanded content; we close them once JS runs. */
  function initFooterAccordion() {
    var footer = document.querySelector('.custom-footer');
    if (!footer || footer.__bettrFooterInit) return;
    footer.__bettrFooterInit = true;
    var columns = footer.querySelectorAll('.custom-footer__column');
    if (!columns.length) return;
    var mq = window.matchMedia('(min-width: 750px)');
    function apply(isDesktop) {
      for (var i = 0; i < columns.length; i++) {
        if (isDesktop) columns[i].setAttribute('open', '');
        else columns[i].removeAttribute('open');
      }
    }
    function onChange(e) { apply(e.matches); }
    apply(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
  }

  /* ── 3. Klaviyo cookie-prefs button ──────────────────────────────────────
     Klaviyo's openCookiePreferences() function exists once their onsite
     script loads, but it only opens a UI if Cookie Banner / Cookie Consent
     Manager is configured in the merchant's Klaviyo admin (Settings →
     Cookies). Without that setup the call silently no-ops and the button
     looks broken.

     Strategy: try Klaviyo's modal, then 700 ms later check whether anything
     modal-like appeared in the DOM. If nothing visible appeared, show our
     own fallback that gives the user actionable info — browser cookie
     settings + the Bettr contact email + a list of the third parties we
     use. Always useful, never silent. */
  function initKlaviyoCookies() {
    var btns = document.querySelectorAll('[data-klaviyo-cookie-prefs]');
    if (!btns.length) return;

    function showFallback() {
      var msg = [
        'Manage cookies on this site',
        '',
        '• Browser settings — Chrome, Safari, Firefox, and Edge each let you block or delete cookies in their privacy preferences. Links are listed in the section below.',
        '• Marketing emails — reply UNSUBSCRIBE to any Bettr email or contact sales@bettrsauna.co to opt out.',
        '',
        'Third parties we use:',
        '  Shopify — essential commerce, always required',
        '  Klaviyo — email marketing',
        '  AfterSell — post-purchase upsells'
      ].join('\n');
      alert(msg);
    }

    function klaviyoModalVisible() {
      var sel = '.klaviyo-cookie-modal, .klaviyo-cookie-banner, [class*="klaviyo"][class*="cookie"], iframe[src*="klaviyo"]';
      var nodes = document.querySelectorAll(sel);
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        if (n.hasAttribute('data-klaviyo-cookie-prefs')) continue;
        if (n.offsetParent !== null && n.getBoundingClientRect().width > 0) return true;
      }
      return false;
    }

    Array.prototype.forEach.call(btns, function (btn) {
      if (btn.__bettrKlavInit) return;
      btn.__bettrKlavInit = true;
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var triedKlaviyo = false;
        try {
          if (window.klaviyo && typeof window.klaviyo.openCookiePreferences === 'function') {
            window.klaviyo.openCookiePreferences();
            triedKlaviyo = true;
          } else if (window._klOnsite && Array.isArray(window._klOnsite)) {
            window._klOnsite.push(['openForm', 'cookie-preferences']);
            triedKlaviyo = true;
          }
        } catch (err) {}

        if (!triedKlaviyo) {
          showFallback();
          return;
        }
        setTimeout(function () {
          if (!klaviyoModalVisible()) showFallback();
        }, 700);
      });
    });
  }

  /* ── 4. Careers team-cards horizontal slider ───────────────────────────── */
  function initCareersSlider() {
    var sections = document.querySelectorAll('.custom-careers-cards-section');
    Array.prototype.forEach.call(sections, function (section) {
      if (section.__bettrCareersInit) return;
      section.__bettrCareersInit = true;
      var track = section.querySelector('[data-careers-track]');
      var prev = section.querySelector('[data-careers-prev]');
      var next = section.querySelector('[data-careers-next]');
      if (!track || !prev || !next) return;

      function step() {
        var first = track.querySelector('.custom-careers-cards__card');
        if (!first) return 320;
        var styles = getComputedStyle(track);
        var gap = parseFloat(styles.columnGap || styles.gap || 24) || 0;
        return first.getBoundingClientRect().width + gap;
      }
      function update() {
        var maxScroll = track.scrollWidth - track.clientWidth - 1;
        prev.disabled = track.scrollLeft <= 0;
        next.disabled = track.scrollLeft >= maxScroll;
      }
      prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
      var rafId = 0;
      track.addEventListener('scroll', function () {
        if (rafId) return;
        rafId = requestAnimationFrame(function () { rafId = 0; update(); });
      }, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ── 5. PDP-ES features fade-in ────────────────────────────────────────── */
  function initPdpEsFeatures() {
    var blocks = document.querySelectorAll('[data-feature-block]:not(.is-visible)');
    if (!blocks.length || !('IntersectionObserver' in window)) return;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    Array.prototype.forEach.call(blocks, function (b) { observer.observe(b); });
  }

  /* ── 6. PDP FAQ accordions (ES + PL share the pattern) ─────────────────── */
  function initPdpFaqAccordions() {
    var sections = document.querySelectorAll('[data-faq-section], [data-pl-faq]');
    Array.prototype.forEach.call(sections, function (section) {
      if (section.__bettrFaqInit) return;
      section.__bettrFaqInit = true;
      var toggles = section.querySelectorAll('[data-faq-toggle]');
      if (!toggles.length) return;
      Array.prototype.forEach.call(toggles, function (toggle) {
        toggle.addEventListener('click', function () {
          var answer = toggle.nextElementSibling;
          var wasOpen = toggle.classList.contains('is-open');
          Array.prototype.forEach.call(toggles, function (t) {
            t.classList.remove('is-open');
            if (t.nextElementSibling) t.nextElementSibling.classList.remove('is-open');
          });
          if (!wasOpen) {
            toggle.classList.add('is-open');
            if (answer) answer.classList.add('is-open');
          }
        });
      });
    });
  }

  /* ── Boot ──────────────────────────────────────────────────────────────── */
  function boot() {
    initAnchorButtons();
    initFooterAccordion();
    initKlaviyoCookies();
    initCareersSlider();
    initPdpEsFeatures();
    initPdpFaqAccordions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
